import type { ReactNode } from "react";
import { Sparkles, type LucideIcon } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { GENERATE_COST_NOTE } from "config/navigation";
import { cn } from "utils/cn";

export interface StepEmptyStateProps {
  icon: LucideIcon;
  title: string;
  /** One sentence on what RivetAI builds. */
  description: string;
  /** Names the action, e.g. "Generate schedule". */
  actionLabel: string;
  /**
   * Opens the Run AI dialog on this pipeline. Null hides the button and the
   * cost note, e.g. for people who cannot run AI.
   */
  onGenerate: (() => void) | null;
  /** A manual way in, e.g. "Add a character", shown next to the button. */
  secondaryAction?: ReactNode;
  /**
   * `page` fills the content area. `inline` is a strip above content that
   * still works without AI, such as the shooting calendar.
   */
  variant?: "page" | "inline";
  className?: string;
}

const GenerateButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button type="button" color="brand" size="sm" onClick={onClick}>
    <Sparkles aria-hidden />
    {label}
  </Button>
);

/** The manual way in next to Generate, e.g. "Add shoot day". Quiet, never amber. */
export const StepEmptyStateSecondaryButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <Button type="button" color="gray" size="sm" onClick={onClick}>
    {children}
  </Button>
);

/**
 * The one empty state of a production step that has not been generated:
 * icon, title, what RivetAI builds, and a named Generate button that opens
 * the Run AI dialog on that pipeline. The dialog shows the cost and needs
 * the user's Run click; this never starts a run.
 */
export const StepEmptyState = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  onGenerate,
  secondaryAction,
  variant = "page",
  className,
}: StepEmptyStateProps) => {
  if (variant === "inline") {
    return (
      <section
        aria-label={title}
        className={cn(
          "flex flex-none flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3",
          className
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-foreground"
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <div className="flex flex-wrap items-center gap-2">
            {secondaryAction}
            {onGenerate ? <GenerateButton label={actionLabel} onClick={onGenerate} /> : null}
          </div>
          {onGenerate ? (
            <span className="text-xs text-muted-foreground">{GENERATE_COST_NOTE}</span>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label={title}
      className={cn(
        "grid min-h-[320px] flex-1 place-items-center rounded-[10px] border border-border bg-card px-4 py-10 text-center",
        className
      )}
    >
      <div className="flex max-w-sm flex-col items-center">
        <span
          aria-hidden
          className="mb-3 grid h-11 w-11 place-items-center rounded-[11px] bg-muted text-foreground"
        >
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="mb-1.5 text-[15px] font-semibold text-foreground">{title}</h2>
        <p className="mb-4 text-[13px] leading-snug text-muted-foreground">{description}</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onGenerate ? <GenerateButton label={actionLabel} onClick={onGenerate} /> : null}
          {secondaryAction}
        </div>
        {onGenerate ? (
          <span className="mt-2 block text-xs text-muted-foreground">{GENERATE_COST_NOTE}</span>
        ) : null}
      </div>
    </section>
  );
};
